import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { XCircle, ArrowRight, RotateCcw, Loader2, AlertCircle, ShoppingCart, Package } from 'lucide-react';
import MetaTags from '@/components/MetaTags';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { supabase } from '@/integrations/supabase/client';
import { getStatusLabel, getStatusDescription, canRetryPayment } from '@/lib/statusMapping';

const PaymentCancel = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id') || searchParams.get('order') || searchParams.get('orderIdClientUsage');
  const transactionId = searchParams.get('transaction_id');
  const source = searchParams.get('source');

  const [orderData, setOrderData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOrderStatus = async () => {
    if (!orderId) return;

    setLoading(true);
    setError(null);

    try {
      console.log('Fetching order status for cancelled payment:', orderId);

      const { data: order, error: orderError } = await supabase
        .from('orders')
        .select('id, status, total_amount, created_at, updated_at')
        .eq('id', orderId)
        .single();

      if (orderError) {
        console.error('Failed to fetch order:', orderError);
        throw new Error('לא ניתן לטעון את פרטי ההזמנה');
      }

      if (order) {
        console.log('Order found for cancelled payment:', order);
        setOrderData(order);
      }
    } catch (err) {
      console.error('Error fetching order status:', err);
      setError(err instanceof Error ? err.message : 'שגיאה בטעינת פרטי ההזמנה');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // CRITICAL: Check if we're in an iframe and break out to parent window
    if (window.self !== window.top) {
      console.log('Payment cancel page detected in iframe - redirecting parent window');

      const currentOrigin = window.location.origin;
      const cancelParams = new URLSearchParams();
      if (orderId) cancelParams.set('orderId', orderId);
      if (source) cancelParams.set('source', source);
      if (transactionId) cancelParams.set('transaction_id', transactionId);

      const cancelUrl = `${currentOrigin}/payment/cancel?${cancelParams.toString()}`;

      try {
        window.top!.location.href = cancelUrl;
        return;
      } catch (error) {
        console.error('Failed to redirect parent window:', error);
      }
    }

    if (orderId) {
      fetchOrderStatus();
    }
  }, [orderId, source, transactionId]);

  // Determine display based on order status
  const getDisplayInfo = () => {
    if (loading) {
      return {
        icon: <Loader2 className="h-16 w-16 text-primary animate-spin" />,
        title: 'טוען פרטי הזמנה...',
        description: 'אנא המתן בזמן שאנו טוענים את פרטי ההזמנה',
        canRetry: false
      };
    }

    if (error) {
      return {
        icon: <AlertCircle className="h-16 w-16 text-destructive" />,
        title: 'שגיאה בטעינת ההזמנה',
        description: error,
        canRetry: false
      };
    }

    if (orderData) {
      const status = orderData.status;
      const isRetryable = canRetryPayment(status);

      if (status === 'cancelled') {
        return {
          icon: <XCircle className="h-16 w-16 text-destructive" />,
          title: 'ההזמנה בוטלה',
          description: 'ההזמנה בוטלה בעקבות כישלון התשלום או ביטול ידני. תוכל לנסות שוב או לחזור לעגלת הקניות.',
          canRetry: isRetryable
        };
      } else if (status === 'pending') {
        return {
          icon: <Package className="h-16 w-16 text-warning" />,
          title: 'התשלום בוטל אך ההזמנה עדיין פעילה',
          description: 'ההזמנה שלך עדיין ממתינה לתשלום. תוכל להשלים את התשלום כעת.',
          canRetry: isRetryable
        };
      } else {
        return {
          icon: <AlertCircle className="h-16 w-16 text-warning" />,
          title: `ההזמנה במצב: ${getStatusLabel(status as any)}`,
          description: getStatusDescription(status as any),
          canRetry: isRetryable
        };
      }
    }

    // Default for payment cancellation without order ID
    return {
      icon: <XCircle className="h-16 w-16 text-warning" />,
      title: 'התשלום בוטל',
      description: 'התשלום בוטל על ידך או שהתרחשה שגיאה במהלך התהליך. תוכל לחזור לעגלת הקניות ולנסות שוב.',
      canRetry: true
    };
  };

  const displayInfo = getDisplayInfo();

  return (
    <>
      <MetaTags
        title="תשלום בוטל - Excess Market"
        description="התשלום בוטל. תוכל לנסות שוב או לחזור לעגלת הקניות."
      />
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto space-y-6">
            {/* Dynamic Status Display */}
            <Card className="text-center">
              <CardHeader>
                <div className="flex justify-center mb-4">
                  {displayInfo.icon}
                </div>
                <CardTitle className={`text-2xl ${orderData?.status === 'cancelled' ? 'text-destructive' : 'text-warning'}`}>
                  {displayInfo.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  {displayInfo.description}
                </p>

                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      {error}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={fetchOrderStatus}
                        className="mt-2"
                      >
                        נסה שוב
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}

                {(orderData?.id || orderId) && (
                  <div className="bg-muted/50 p-4 rounded-lg">
                    <div className="flex items-center justify-center gap-2">
                      <Package className="h-5 w-5 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">מספר הזמנה:</span>
                      <span className="font-mono font-bold">{orderData?.id || orderId}</span>
                    </div>
                    {orderData?.status && (
                      <div className="mt-2">
                        <span className="text-sm text-muted-foreground">סטטוס: </span>
                        <span className="font-medium">{getStatusLabel(orderData.status as any)}</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  {displayInfo.canRetry && orderId && (
                    <Button asChild disabled={loading}>
                      <Link to={`/checkout?retry=${orderId}`}>
                        <RotateCcw className="mr-2 h-4 w-4" />
                        נסה תשלום שוב
                      </Link>
                    </Button>
                  )}

                  {displayInfo.canRetry && !orderId && (
                    <Button asChild disabled={loading}>
                      <Link to="/products">
                        <ShoppingCart className="mr-2 h-4 w-4" />
                        חזור לקניות
                      </Link>
                    </Button>
                  )}

                  <Button variant="outline" asChild disabled={loading}>
                    <Link to="/">
                      חזור לעמוד הבית
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Troubleshooting - only show if not loading */}
            {!loading && (
              <>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">מדוע התשלום בוטל?</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="space-y-3 text-sm">
                      <div className="flex items-start gap-3">
                        <div className="bg-muted rounded-full p-1 mt-1">
                          <div className="w-2 h-2 bg-muted-foreground rounded-full"></div>
                        </div>
                        <div>
                          <p className="font-medium">ביטול ידני</p>
                          <p className="text-muted-foreground">לחצת על כפתור הביטול באתר התשלומים</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="bg-muted rounded-full p-1 mt-1">
                          <div className="w-2 h-2 bg-muted-foreground rounded-full"></div>
                        </div>
                        <div>
                          <p className="font-medium">בעיה טכנית</p>
                          <p className="text-muted-foreground">התרחשה שגיאה במערכת התשלומים</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="bg-muted rounded-full p-1 mt-1">
                          <div className="w-2 h-2 bg-muted-foreground rounded-full"></div>
                        </div>
                        <div>
                          <p className="font-medium">פג זמן התשלום</p>
                          <p className="text-muted-foreground">עבר יותר מדי זמן מתחילת התהליך</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="bg-muted rounded-full p-1 mt-1">
                          <div className="w-2 h-2 bg-muted-foreground rounded-full"></div>
                        </div>
                        <div>
                          <p className="font-medium">בעיית רשת</p>
                          <p className="text-muted-foreground">אובדן קישור לאינטרנט במהלך התהליך</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Tips for Success */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">טיפים להצלחת התשלום</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="space-y-3 text-sm">
                      <div className="flex items-start gap-3">
                        <div className="bg-success/10 rounded-full p-1 mt-1">
                          <div className="w-2 h-2 bg-success rounded-full"></div>
                        </div>
                        <div>
                          <p className="font-medium">וודא חיבור יציב לאינטרנט</p>
                          <p className="text-muted-foreground">חיבור איטי או לא יציב עלול לגרום לכישלון</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="bg-success/10 rounded-full p-1 mt-1">
                          <div className="w-2 h-2 bg-success rounded-full"></div>
                        </div>
                        <div>
                          <p className="font-medium">בדוק פרטי הכרטיס</p>
                          <p className="text-muted-foreground">וודא שמספר הכרטיס, תאריך התפוגה וקוד האבטחה נכונים</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="bg-success/10 rounded-full p-1 mt-1">
                          <div className="w-2 h-2 bg-success rounded-full"></div>
                        </div>
                        <div>
                          <p className="font-medium">השתמש בדפדפן מעודכן</p>
                          <p className="text-muted-foreground">דפדפן ישן עלול לגרום לבעיות תאימות</p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <div className="bg-success/10 rounded-full p-1 mt-1">
                          <div className="w-2 h-2 bg-success rounded-full"></div>
                        </div>
                        <div>
                          <p className="font-medium">אל תרענן את הדף</p>
                          <p className="text-muted-foreground">המתן בסבלנות עד השלמת התהליך</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

            {/* Support Information */}
            <Card>
              <CardContent className="pt-6">
                <div className="text-center space-y-2">
                  <h4 className="font-medium">זקוק לעזרה?</h4>
                  <p className="text-sm text-muted-foreground">
                    הצוות שלנו כאן לעזור לך להשלים את הרכישה
                    {orderId && (
                      <>
                        <br />מספר הזמנה: <span className="font-mono">{orderId}</span>
                      </>
                    )}
                  </p>
                  <Button variant="outline" size="sm" asChild disabled={loading}>
                    <Link to="/contact">
                      צור קשר לתמיכה
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default PaymentCancel;